import { getTournamentGroup } from "@ssf/memdb-api";
import { type TournamentRoom, tournamentRoomSchema, z } from "@ssf/play-common";
import {
  orderByChild,
  roomsRef,
  startAt,
  useValue,
} from "@ssf/play-components";
import { useQuery } from "@tanstack/react-query";
import { isToday, parseISO } from "date-fns";
import { useMemo } from "react";
import type { TournamentListItem } from "../components/TournamentListCard.js";
import { getTournamentTimeRange } from "./tournamentTimeRange.js";

const PLAY_WEB_URL = process.env.PLAY_WEB_URL;
const queryConstraints = [orderByChild("memdbGroupId"), startAt(0)];
const roomSchema = z.record(tournamentRoomSchema);
const tournamentGroupSchema = {
  id: 1,
  start: 1,
  end: 1,
  arenaStart: 1,
  arenaEnd: 1,
} as const;

export const useTodaysTournaments = () => {
  const { data: rooms = {}, isFetched: areRoomsFetched } = useValue(roomsRef)({
    queryConstraints,
    schema: roomSchema,
  });

  const publicRooms = useMemo(
    () =>
      (Object.entries(rooms) as [string, TournamentRoom][])
        .filter(
          ([, room]) =>
            room.privacyStatus === "public" ||
            room.privacyStatus === "publicAlways",
        )
        .sort(
          (a, b) =>
            (b[1].updated?.getTime() ?? 0) - (a[1].updated?.getTime() ?? 0),
        ),
    [rooms],
  );

  const memdbGroupIds = useMemo(
    () => [
      ...new Set(
        publicRooms
          .map(([, room]) => room.memdbGroupId)
          .filter(
            (groupId): groupId is number =>
              typeof groupId === "number" && groupId > 0,
          ),
      ),
    ],
    [publicRooms],
  );

  const { data: groupMap = {}, isPending: areGroupsPending } = useQuery({
    queryKey: ["tournamentGroups", memdbGroupIds],
    queryFn: async () => {
      const groups = await Promise.all(
        memdbGroupIds.map((groupId) =>
          getTournamentGroup({
            groupId,
            params: { schema: tournamentGroupSchema },
          }).catch(() => null),
        ),
      );
      const groupMap: Record<number, NonNullable<(typeof groups)[number]>> = {};
      for (const group of groups) {
        if (group) groupMap[group.id] = group;
      }
      return groupMap;
    },
    enabled: memdbGroupIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const tournaments = useMemo<TournamentListItem[]>(
    () =>
      publicRooms.flatMap(([key, room]) => {
        if (!room.memdbGroupId) return [];

        const group = groupMap[room.memdbGroupId];
        const start = group?.start;
        if (!start || !isToday(parseISO(start))) return [];

        return [
          {
            id: key,
            name: room.name || key,
            start,
            timeRange: getTournamentTimeRange(group),
            href: `${PLAY_WEB_URL}/${key}`,
          },
        ];
      }),
    [publicRooms, groupMap],
  );

  return {
    tournaments,
    isLoading:
      !areRoomsFetched || (memdbGroupIds.length > 0 && areGroupsPending),
  };
};
